import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Order } from "../../../services";
import OrdersContent from "../../../components/orders/content";
import SectionItem from "../../../components/orders-new/section-item";
import OrdersSectionItemTable from "../../../components/orders-new/section-item/components/OrdersSectionItemTable";
import DefaultPaginationNew from "../../../components/shared/DefaultPaginationNew";
import Loading from "../../../components/shared/Loading/Loading";
import OrdersTab from "../../../components/orders-new/tabs/OrdersTab";
import HeaderContentNew from "../../../components/shared/HeaderContentNew/HeaderContentNew";
import OrderFilter from "../../../components/orders-new/filter";
import moment from "moment";

const ListsPage = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [statusActive, setStatus] = useState(null);
  const [sorting, setSorting] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [currentPage, changePage] = useState(1);
  const [selectedLimit, changeLimit] = useState(10);
  const [forceUpdate, setForceUpdate] = useState(0);

  const isExpeditor = user?.type === "Expeditor";
  const companyId = user.companyId === null ? "" : user.companyId;
  const [exportingExcel, setExportingExcel] = useState(false);
  const [isCallTaps, setCallTaps] = useState(false);

  // filter
  const [orderNumber, setOrderNumber] = useState("");
  const [cities, setCities] = useState([]);
  const [cityFrom, setCityFrom] = useState("");
  const [cityTo, setCityTo] = useState("");
  const [loadingDate, setLoadingDate] = useState("");
  const [unloadingDate, setUnloadingDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const getFilterParams = () => {
    const params = {
      companyId,
      status: !statusActive || statusActive.status === "all" ? "" : statusActive.status,
      page: currentPage,
      limit: selectedLimit,
    };

    if (orderNumber) params.orderNo = orderNumber;
    if (cityFrom) params.cityIdFrom = cityFrom.id;
    if (cityTo) params.cityIdTo = cityTo.id;
    if (loadingDate) params.startShippingDate = moment(loadingDate).format("YYYY-MM-DD");
    if (unloadingDate)
      params.endShippingDate = moment(unloadingDate).format("YYYY-MM-DD");
    if (selectedStatus && statusActive.status === "all")
      params.status = selectedStatus.value;

    if (sorting) {
      for (const sortingKey in sorting) {
        params.sort = `${sortingKey},${sorting[sortingKey]}`;
      }
    } else {
      params.sort = "createdAt,desc";
    }

    return params;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = getFilterParams();
      // console.log("Request Params:", params);

      const data = await getOwnOrders(params);
      setOrders(data.rows || []);
      setOrdersTotal(data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getPropsFilter = () => ({
    orderNumber,
    setOrderNumber,
    cities,
    cityFrom,
    setCityFrom,
    cityTo,
    setCityTo,
    loadingDate,
    setLoadingDate,
    unloadingDate,
    setUnloadingDate,
    selectedStatus,
    setSelectedStatus,
    statusActive,
  });

  const handleRefetchData = () => {
    setOrders([]);
    fetchData();
    setCallTaps(!isCallTaps);
  };

  const getOwnOrders = async (params = {}) =>
    Order.getOwnActiveOrders(params)
      .then((res) => res.data)
      .catch((e) => e);

  const handleDownloadExcel = () => {
    setExportingExcel(true);
    const params = {
      sort: statusActive.status,
      page: currentPage,
      limit: selectedLimit,
    };

    Order.getClientExportOrders(params)
      .then((response) => {
        if (response.status === 200) {
          const file = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          var downloadElement = document.createElement("a");
          downloadElement.href = URL.createObjectURL(file);
          downloadElement.download = "client_export_orders.xlsx";
          document.body.appendChild(downloadElement);
          downloadElement.click();
          document.body.removeChild(downloadElement);
        } else {
          alert(t('common.toast.genericError'));
        }
      })
      .catch((error) => {
        console.error("Error downloading Excel:", error);
        alert(t('common.toast.genericError'));
      })
      .finally(() => {
        setExportingExcel(false);
      });
  };

  useEffect(() => {
    const delayFetch = setTimeout(() => {
      fetchData();
    }, 500);
  
    return () => clearTimeout(delayFetch);
  }, [cityFrom, cityTo, loadingDate, unloadingDate, selectedStatus, selectedLimit, orderNumber]);

  useEffect(() => {
    if (statusActive) fetchData();
  }, [statusActive, currentPage, sorting, forceUpdate]);

  useEffect(() => {
    Order.getAllCities()
      .then((res) => res.data)
      .then((data) => {
        const cities = data?.sort((a, b) => a.name.localeCompare(b.name));
        setCities(cities);
      })
      .catch((error) => {
        console.error("Error getting cities:", error);
      });
  }, []);

  if (user.type !== "Client") {
    return (
      <div className="alert alert-warning text-center" role="alert">
        <strong>{t('common.warning')}</strong> {t('ordersList.errors.pageNotAvailableForTransporter')}
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <HeaderContentNew
        displayReloadBtn
        title={t('ordersList.pageTitle')}
        displayCreateBtn
        onReload={handleRefetchData}
      />

      {/* Filter */}
      <OrderFilter onRefetchData={handleRefetchData} {...getPropsFilter()} />

      {/* Tabs */}
      <OrdersTab
        setStatus={setStatus}
        changeSortColumn={setSorting}
        sortColumn={sorting}
        onDownloadExcel={handleDownloadExcel}
        isCallTaps={isCallTaps}
        cityFrom={cityFrom}
        cityTo={cityTo}
        loadingDate={loadingDate}
        unloadingDate={unloadingDate}
        selectedStatus={selectedStatus}
        statusActive={statusActive}
      />
      {!statusActive && <Loading inBlock />}
      {/* All content */}
      {statusActive && (
        <>
          <OrdersContent>
            {/* First Section */}
            <SectionItem>
              {/* <OrdersSectionItemTitle title={statusActive.statusName} count={statusActive.count} /> */}
              {/* TODO filters */}
              <OrdersSectionItemTable
                items={orders || []}
                isLoading={isLoading}
                isExpeditor={isExpeditor}
                companyName={user?.companyName}
                onForceUpdate={() => setForceUpdate((prev) => prev + 1)}
              />
              {/* <OrderSectionItemTableMobile
                sortColumn={sorting}
                items={orders || []}
                isLoading={isLoading}
                isExpeditor={isExpeditor}
              /> */}

              {orders && orders.length ? (
                <DefaultPaginationNew
                  total={ordersTotal}
                  currentPage={currentPage}
                  limit={selectedLimit}
                  changePage={changePage}
                  changeLimit={changeLimit}
                />
              ) : null}
            </SectionItem>
          </OrdersContent>
        </>
      )}
    </>
  );
};

export default ListsPage;
